import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { EvidenceLabel, ImageLabelsSidecar } from '@sr-test-wizard/shared';
import { runAssetsApi, type RunImage } from '../../lib/runAssets';
import { evidenceLabelsApi } from '../../lib/evidenceLabelsApi';

interface Props {
  runId: string;
  onClose: () => void;
}

/**
 * Modal that mirrors `source-code-helper-main/frontend/src/components/ImagesViewer.tsx`:
 * thumbnail grid on the left, vision-analysis panel on the right, click-to-zoom modal.
 * Driven off our `/api/runs/:id/images` and `/images/analysis` endpoints.
 */
export function ImagesGalleryDialog({ runId, onClose }: Props) {
  const [images, setImages] = useState<RunImage[]>([]);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [analysisExists, setAnalysisExists] = useState(false);
  const [zoom, setZoom] = useState<RunImage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageLabels, setImageLabels] = useState<ImageLabelsSidecar | null>(null);
  const [labelDefs, setLabelDefs] = useState<EvidenceLabel[]>([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      runAssetsApi.listImages(runId),
      runAssetsApi.loadImagesAnalysis(runId),
      runAssetsApi.loadImageLabels(runId).catch(() => null),
      evidenceLabelsApi.list().catch(() => null),
    ])
      .then(([imgs, ana, labels, defs]) => {
        setImages(imgs.items);
        setAnalysisExists(ana.exists);
        setAnalysis(ana.content);
        setImageLabels(labels);
        if (defs) setLabelDefs(defs.entries);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [runId]);

  const labelDefByKey = useMemo(() => {
    const m = new Map<string, EvidenceLabel>();
    for (const def of labelDefs) m.set(def.key.toLowerCase(), def);
    return m;
  }, [labelDefs]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal images-gallery-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        <div className="row" style={{ marginBottom: 12 }}>
          <strong>Game-Help Screenshots</strong>
          <span className="muted small" style={{ marginLeft: 8 }}>
            {images.length} images
          </span>
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>

        {loading && <p className="muted">Loading images...</p>}
        {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}

        {!loading && (
          <div className="images-gallery">
            <div className="image-grid">
              {images.length === 0 ? (
                <p className="muted">No images downloaded yet.</p>
              ) : (
                images.map((img) => {
                  const entry = imageLabels?.entries[img.name];
                  const labels = entry?.labels ?? [];
                  return (
                    <button
                      key={img.path}
                      className="image-thumb"
                      onClick={() => setZoom(img)}
                      title={img.name}
                    >
                      <img src={img.url} alt={img.name} loading="lazy" />
                      <div className="image-thumb-name">{img.name}</div>
                      {labels.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                            padding: '4px 6px 6px',
                          }}
                        >
                          {labels.map((l) => {
                            const def = labelDefByKey.get(l.toLowerCase());
                            return (
                              <span
                                key={l}
                                className="badge small"
                                title={def?.definition ?? l}
                                style={{
                                  fontFamily: 'monospace',
                                  fontSize: 10,
                                  background: 'rgba(99, 102, 241, 0.12)',
                                  border: '1px solid rgba(99, 102, 241, 0.4)',
                                }}
                              >
                                {l}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
            <div className="analysis-panel">
              <div className="row" style={{ marginBottom: 8 }}>
                <strong>Image Analysis</strong>
              </div>
              {analysisExists && analysis ? (
                <div className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{analysis}</ReactMarkdown>
                </div>
              ) : (
                <p className="muted small">No image analysis available yet.</p>
              )}
            </div>
          </div>
        )}

        {zoom && (
          <div className="modal-backdrop image-zoom-backdrop" onClick={() => setZoom(null)}>
            <div className="image-zoom" onClick={(e) => e.stopPropagation()}>
              <div className="row" style={{ marginBottom: 8 }}>
                <strong>{zoom.name}</strong>
                <span className="spacer" />
                <button className="btn btn-ghost" onClick={() => setZoom(null)}>Close</button>
              </div>
              <img src={zoom.url} alt={zoom.name} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
